import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';
import { fetchConfig } from '@theme/utilities';
import { getScrollEventTarget, scrollContainerMediaQuery } from '@theme/scroll-container';

/**
 * The whole-catalog page and the categories (block _collection-groups, snippet olin-shelf-card):
 * - «+» on a light card adds the product to the cart without leaving the page and turns into
 *   «− n +»; the cart icon and the cart drawer hear about it through the theme's cart event, the
 *   same way the product form and the cart drawer tell each other (assets/component-cart-items.js);
 * - the sticky row of columns shows how many products of each column are in the cart (a column
 *   with something in it shrinks to its icon and the number) and marks the column on screen;
 * - the «Ще знадобиться…» line (snippet olin-cart-hints) names the most important rule of
 *   «Підказки для кошика» that fits the cart: under the row on a computer, at the bottom of the
 *   screen on a phone; «Вже маю» skips a rule for good (this browser), the cross hides the line
 *   until the next visit.
 *
 * A tap changes the card's number and the column count at once, with the theme's flight to the
 * cart and a small animation of the button; the cart gets one change per card, with the final
 * quantity, once the taps on it pause for PAUSE ms. Cart requests go one by one, and each answers
 * with the whole cart, so the numbers settle on what the cart really has. Quantities come from the
 * cart itself, so a change made in the cart drawer shows up here too. One listener serves every
 * card: the shelves hold a few hundred of them.
 */

const SOURCE = 'olin-catalog';

/** Taps on one card closer than this (ms) go to the cart as one change */
const PAUSE = 400;

/** The theme's «add to cart» animation is on (theme settings, passed on this script's tag) */
const flies =
  document.querySelector('script[data-add-to-cart-animation]')?.getAttribute('data-add-to-cart-animation') === 'true';

/** The section that says which rules fit the cart (sections/olin-cart-hints.liquid) */
const HINT_SECTION = 'olin-cart-hints';
const HINT_SKIPPED = 'olin.cartHints.skipped';
const HINT_CLOSED = 'olin.cartHints.closed';

/** @type {string[]} handles of the rules that fit the cart, most important first */
let hintList = [];

/**
 * @type {Map<string, { quantity: number, key: string, first: number }>} variant id → its lines in
 * the cart: the quantity of all of them, the key and the quantity of the first one
 */
let lines = new Map();

/** @type {Map<string, number>} variant id → the quantity tapped on its card, not in the cart yet */
const wanted = new Map();

/** @type {Map<string, number>} variant id → the pause running before its change goes out */
const timers = new Map();

/** Variant ids whose change waits in the queue */
const queued = new Set();

/** @type {Promise<unknown>} the cart requests, one after another */
let queue = Promise.resolve();

/** Events this script dispatched itself, so its own updates are not fetched twice */
const ownEvents = new WeakSet();

/** @returns {HTMLElement[]} */
const controls = () => [...document.querySelectorAll('.olin-quick-add[data-variant-id]')].filter(
  (element) => element instanceof HTMLElement
);

/**
 * What a card shows: its taps not sent yet, else the cart.
 * @param {string} id - variant id
 */
const shown = (id) => wanted.get(id) ?? lines.get(id)?.quantity ?? 0;

/** @returns {string[]} */
function cartSectionIds() {
  return [...document.querySelectorAll('cart-items-component')]
    .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId ?? '' : ''))
    .filter(Boolean);
}

/** @returns {HTMLElement | null} */
function hintBar() {
  const bar = document.querySelector('[data-olin-hint]');
  return bar instanceof HTMLElement ? bar : null;
}

/** @returns {Promise<any>} */
async function fetchCart() {
  const response = await fetch(`${Theme.routes.cart_url}.js`, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  });
  return response.json();
}

/** @param {any} cart */
function readCart(cart) {
  lines = new Map();
  for (const item of cart?.items ?? []) {
    const id = String(item.variant_id);
    const line = lines.get(id);
    // A variant can sit in two lines (different properties): the card counts both, changes the first
    lines.set(
      id,
      line
        ? { ...line, quantity: line.quantity + item.quantity }
        : { quantity: item.quantity, key: item.key, first: item.quantity }
    );
  }
}

/** Shows every card's quantity and every column's count */
function render() {
  for (const control of controls()) renderControl(control);
  renderColumns();
}

/** @param {HTMLElement} control */
function renderControl(control) {
  const quantity = String(shown(control.dataset.variantId ?? ''));
  if (control.dataset.qty === quantity) return;
  control.dataset.qty = quantity;
  const count = control.querySelector('.olin-quick-add__count');
  if (count) count.textContent = quantity;
}

/** Counts in the sticky row: products of each column in the cart */
function renderColumns() {
  for (const chip of document.querySelectorAll('.collection-groups__nav a.olin-map__chip[href^="#"]')) {
    if (!(chip instanceof HTMLElement)) continue;

    const group = document.getElementById(chip.getAttribute('href')?.slice(1) ?? '');
    let total = 0;
    if (group) {
      for (const control of group.querySelectorAll('.olin-quick-add[data-variant-id]')) {
        if (control instanceof HTMLElement) total += shown(control.dataset.variantId ?? '');
      }
    }

    let badge = chip.querySelector('.collection-groups__nav-count');
    if (total > 0) {
      if (!badge) {
        badge = document.createElement('span');
        badge.className = 'collection-groups__nav-count';
        chip.append(badge);
      }
      badge.textContent = String(total);
    } else {
      badge?.remove();
    }
    chip.classList.toggle('is-taken', total > 0);
  }
}

/**
 * A tap on «+» or «−»: the card and its column count change at once, the cart after the pause.
 * @param {HTMLElement} control
 * @param {number} delta - 1 or -1
 */
function tap(control, delta) {
  const id = control.dataset.variantId ?? '';
  if (!id) return;

  const focused = control.contains(document.activeElement);
  const before = shown(id);
  const after = Math.max(0, before + delta);
  wanted.set(id, after);
  for (const card of controls()) {
    if (card.dataset.variantId === id) renderControl(card);
  }
  renderColumns();
  if (focused) keepFocus(control);

  if (after > before) fly(control);
  if (before === 0 && after > 0) unfold(control);
  else if (before > 0 && after === 0) fold(control);
  else if (after !== before) bump(control, delta);

  window.clearTimeout(timers.get(id));
  timers.set(
    id,
    window.setTimeout(() => {
      timers.delete(id);
      send(id);
    }, PAUSE)
  );
}

/**
 * The theme's flight to the cart (assets/fly-to-cart.js): the card's photo flies to the cart icon.
 * @param {HTMLElement} control
 */
function fly(control) {
  if (!flies || still() || !customElements.get('fly-to-cart')) return;
  const cartIcon = document.querySelector('.header-actions__cart-icon');
  const plus = control.querySelector('.olin-quick-add__plus');
  const photo = control.closest('.olin-shelf-card')?.querySelector('.olin-shelf-card__image');
  const image = photo instanceof HTMLImageElement ? photo.currentSrc || photo.src : '';
  if (!cartIcon || !plus || !image) return;

  const flight = /** @type {HTMLElement & { source?: Element, destination?: Element }} */ (
    document.createElement('fly-to-cart')
  );
  flight.classList.add('fly-to-cart--quick');
  flight.style.setProperty('background-image', `url("${image}")`);
  flight.style.setProperty('--start-opacity', '0');
  // From the «+» the finger is on: after the tap it is the one in «− n +»
  flight.source = plus;
  flight.destination = cartIcon;
  document.body.append(flight);
}

/**
 * The first one in: the «+» circle stretches to the left into «− 1 +», rays fly out of it.
 * @param {HTMLElement} control
 */
function unfold(control) {
  if (still()) return;
  const pill = control.querySelector('.olin-quick-add__qty');
  if (!(pill instanceof HTMLElement)) return;

  const circle = Math.max(0, pill.offsetWidth - 40);
  pill.animate([{ clipPath: `inset(0 0 0 ${circle}px round 999px)` }, { clipPath: 'inset(0 0 0 0 round 999px)' }], {
    duration: 260,
    easing: 'cubic-bezier(0.2, 0.8, 0.2, 1)',
  });

  const burst = document.createElement('span');
  burst.className = 'olin-quick-add__burst';
  burst.setAttribute('aria-hidden', 'true');
  for (let ray = 0; ray < 8; ray += 1) {
    const line = document.createElement('span');
    line.style.setProperty('--angle', `${ray * 45}deg`);
    burst.append(line);
  }
  control.append(burst);
  window.setTimeout(() => burst.remove(), 600);
}

/**
 * The last one out: «− 1 +» gives way to the «+» circle, which grows back.
 * @param {HTMLElement} control
 */
function fold(control) {
  if (still()) return;
  control.querySelector('.olin-quick-add__add')?.animate(
    [
      { transform: 'scale(0.6)', opacity: 0 },
      { transform: 'none', opacity: 1 },
    ],
    { duration: 200, easing: 'cubic-bezier(0.2, 0.8, 0.2, 1)' }
  );
}

/**
 * The number comes in from below on «+», from above on «−».
 * @param {HTMLElement} control
 * @param {number} delta
 */
function bump(control, delta) {
  if (still()) return;
  control.querySelector('.olin-quick-add__count')?.animate(
    [
      { transform: `translateY(${delta > 0 ? 6 : -6}px)`, opacity: 0.2 },
      { transform: 'none', opacity: 1 },
    ],
    { duration: 180, easing: 'ease-out' }
  );
}

/**
 * «+» turns into «− n +» and back: the focus goes to the button that took the pressed one's place.
 * @param {HTMLElement} control
 */
function keepFocus(control) {
  const active = document.activeElement;
  if (active instanceof HTMLElement && control.contains(active) && active.offsetParent !== null) return;
  const next = control.querySelector(control.dataset.qty === '0' ? '.olin-quick-add__add' : '.olin-quick-add__plus');
  if (next instanceof HTMLElement) next.focus();
}

/**
 * The same after the cart's answer, which can turn a card back (not enough in stock).
 * @param {string} id - variant id
 */
function keepFocusOn(id) {
  for (const control of controls()) {
    if (control.dataset.variantId === id && control.contains(document.activeElement)) keepFocus(control);
  }
}

/**
 * @template T
 * @param {() => Promise<T>} task
 * @returns {Promise<T>}
 */
function enqueue(task) {
  const run = queue.then(task);
  queue = run.catch(() => {});
  return run;
}

/** @param {string} id - variant id */
function send(id) {
  if (queued.has(id)) return;
  queued.add(id);
  enqueue(() => sync(id));
}

/**
 * Puts the quantity tapped on a card into the cart, as one change.
 * @param {string} id - variant id
 */
async function sync(id) {
  queued.delete(id);
  const target = wanted.get(id);
  if (target === undefined) return;

  // A quantity printed with the page has no line key yet: read the cart first
  if (lines.get(id) && !lines.get(id)?.key) {
    try {
      readCart(await fetchCart());
    } catch (_) {
      // Then the change goes by the variant
    }
  }

  const line = lines.get(id);
  const before = line?.quantity ?? 0;
  if (target === before) {
    wanted.delete(id);
    render();
    keepFocusOn(id);
    return;
  }

  // The first line takes the change; lines of the same product with other properties stay
  const quantity = line?.key ? Math.max(0, target - (before - line.first)) : target;
  // The cart drawer's sections, and the hint's one, rendered with the changed cart
  const sections = [...cartSectionIds(), ...(hintBar() ? [HINT_SECTION] : [])].join(',');
  const deferred = CartLinesUpdateEvent.createPromise();
  const event = new CartLinesUpdateEvent(
    line?.key
      ? {
          action: quantity > 0 ? 'update' : 'remove',
          context: 'product',
          lines: [{ id: line.key, quantity }],
          promise: deferred.promise,
        }
      : {
          action: 'add',
          context: 'product',
          lines: [{ merchandiseId: id, quantity: target }],
          promise: deferred.promise,
        }
  );
  ownEvents.add(event);
  document.dispatchEvent(event);

  // Both answer with the whole cart: a line by its key, a new product by its variant
  const url = line?.key ? Theme.routes.cart_change_url : `${Theme.routes.cart_update_url}.js`;
  const change = line?.key ? { id: line.key, quantity } : { updates: { [id]: target } };

  try {
    const response = await fetch(
      url,
      fetchConfig('json', {
        body: JSON.stringify({ ...change, sections, sections_url: window.location.pathname }),
      })
    );
    const data = await response.json();
    const failed = !response.ok || Boolean(data.status);
    const cart = failed ? await fetchCart() : data;
    readCart(cart);
    if (!failed) readHintList(data.sections?.[HINT_SECTION]);

    // Done, unless more taps came meanwhile; less than asked (not enough in stock) shakes the card
    if (wanted.get(id) === target) {
      wanted.delete(id);
      if ((lines.get(id)?.quantity ?? 0) !== target) refuse(id);
    }
    render();
    keepFocusOn(id);
    renderHint();

    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        source: SOURCE,
        sourceId: id,
        itemCount: Math.abs(target - before),
        didError: failed,
        sections: failed ? undefined : data.sections,
      },
    });
  } catch (error) {
    // No answer: the card goes back to what the cart had
    wanted.delete(id);
    render();
    keepFocusOn(id);
    refuse(id);
    deferred.reject(error);
  }

  // Taps made while the request went have waited their pause already
  if (wanted.has(id) && !timers.has(id)) send(id);
}

/** Pauses cut short: something else was clicked (the cart icon, a link), the changes go now */
function flush() {
  for (const [id, timer] of timers) {
    window.clearTimeout(timer);
    timers.delete(id);
    send(id);
  }
}

/** The page is being left: what isn't in the cart yet goes in one request that outlives the page */
function flushOnLeave() {
  for (const timer of timers.values()) window.clearTimeout(timer);
  timers.clear();

  /** @type {Record<string, number>} */
  const updates = {};
  for (const [id, target] of wanted) {
    const line = lines.get(id);
    const before = line?.quantity ?? 0;
    if (target === before) continue;
    if (line?.key) updates[line.key] = Math.max(0, target - (before - line.first));
    else updates[id] = target;
  }
  if (!Object.keys(updates).length) return;

  fetch(`${Theme.routes.cart_update_url}.js`, {
    ...fetchConfig('json', { body: JSON.stringify({ updates }) }),
    keepalive: true,
  }).catch(() => {});
}

/** @param {string} id - variant id */
function refuse(id) {
  for (const control of controls()) {
    if (control.dataset.variantId !== id) continue;
    control.classList.remove('is-refused');
    void control.offsetWidth;
    control.classList.add('is-refused');
  }
}

/** @param {MouseEvent} event */
function onClick(event) {
  const target = event.target instanceof Element ? event.target : null;
  const button = target?.closest('[data-quick-add]');
  const control = button?.closest('.olin-quick-add');
  if (button instanceof HTMLElement && control instanceof HTMLElement) {
    event.preventDefault();
    tap(control, Number(button.dataset.quickAdd) || 1);
    return;
  }

  flush();

  const hint = target?.closest('[data-olin-hint-have], [data-olin-hint-close], [data-olin-hint-link]');
  if (hint instanceof HTMLElement) onHintClick(event, hint);
}

/**
 * Shelves on the page, at its start or after a filter is cleared: the numbers printed with them
 * first (the cart when the HTML was made), then the cart as it is now.
 */
function adopt() {
  lines = new Map();
  for (const control of controls()) {
    const quantity = Number(control.dataset.qty) || 0;
    if (quantity) lines.set(control.dataset.variantId ?? '', { quantity, key: '', first: quantity });
  }
  hintList = (hintBar()?.dataset.olinHintList ?? '').split(',').filter(Boolean);
  render();
  renderHint();
  markColumn();
  refresh();
}

/**
 * Changes made elsewhere (the cart drawer, another tab's page show): read the cart again, and with
 * `hints` also which rules fit it now. In the queue, so it never answers with a cart older than a
 * change of this page.
 * @param {boolean} [hints]
 */
function refresh(hints = false) {
  if (!controls().length) return Promise.resolve();
  return enqueue(async () => {
    try {
      const [cart, section] = await Promise.all([fetchCart(), hints && hintBar() ? fetchHintSection() : null]);
      readCart(cart);
      render();
      if (section !== null) readHintList(section);
      renderHint();
    } catch (_) {
      // The numbers printed with the page stay
    }
  });
}

/** @param {Event} event */
function onCartLinesUpdate(event) {
  if (ownEvents.has(event)) return;
  /** @type {any} */ (event).promise?.then(() => refresh(true)).catch(() => {});
}

/** @returns {Promise<string>} */
async function fetchHintSection() {
  const root = window.Shopify?.routes?.root ?? '/';
  const response = await fetch(`${root}?sections=${HINT_SECTION}`, { credentials: 'same-origin' });
  const data = await response.json();
  return data?.[HINT_SECTION] ?? '';
}

/**
 * Takes the rule list out of the section's HTML; keeps the old one when there is none.
 * @param {string | undefined} html
 */
function readHintList(html) {
  const match = /data-olin-hint-list="([^"]*)"/.exec(html ?? '');
  if (match) hintList = (match[1] ?? '').split(',').filter(Boolean);
}

/** @returns {Record<string, { label: string, why: string, url: string, collection: string }>} */
function hintRules() {
  const script = document.querySelector('script[data-olin-hint-rules]');
  try {
    return JSON.parse(script?.textContent ?? '{}');
  } catch (_) {
    return {};
  }
}

/**
 * @param {Storage} storage
 * @param {string} key
 */
function stored(storage, key) {
  try {
    return storage.getItem(key);
  } catch (_) {
    // Storage can be off (private mode, blocked site data): nothing is remembered then
    return null;
  }
}

/**
 * @param {Storage} storage
 * @param {string} key
 * @param {string} value
 */
function store(storage, key, value) {
  try {
    storage.setItem(key, value);
  } catch (_) {
    // Remembered for this page only
  }
}

/** @type {Set<string> | null} rules marked «Вже маю» when storage is off */
let skippedHere = null;

/** @returns {Set<string>} */
function skipped() {
  if (skippedHere) return skippedHere;
  try {
    const list = JSON.parse(stored(localStorage, HINT_SKIPPED) ?? '[]');
    return new Set(Array.isArray(list) ? list.map(String) : []);
  } catch (_) {
    return new Set();
  }
}

/** @returns {boolean} the visitor asked the system for less motion */
const still = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/** Each show, hide or swap of the line takes a number; an older one that finishes late gives way */
let hintTurn = 0;

/**
 * The first rule that fits the cart and isn't skipped, unless the line was closed.
 * @param {HTMLElement} bar
 * @returns {string | undefined}
 */
function nextHint(bar) {
  const rules = hintRules();
  const skip = skipped();
  const closed = stored(sessionStorage, HINT_CLOSED) === '1' || bar.dataset.closed === 'true';
  return closed ? undefined : hintList.find((name) => rules[name]?.label && !skip.has(name));
}

/** Shows the first rule that fits the cart and isn't skipped, or hides the line */
function renderHint() {
  const bar = hintBar();
  if (!bar) return;

  const handle = nextHint(bar);
  const showing = !bar.hidden && bar.dataset.leaving !== 'true';
  if (!handle) {
    if (showing) hideHint(bar);
  } else if (!showing) {
    fillHint(bar, handle);
    showHint(bar);
  } else if (bar.dataset.rule !== handle) {
    swapHint(bar);
  }
}

/**
 * @param {HTMLElement} bar
 * @param {string} handle
 */
function fillHint(bar, handle) {
  const rule = hintRules()[handle];
  if (!rule) return;

  bar.dataset.rule = handle;
  const label = bar.querySelector('.olin-hint__label');
  const why = bar.querySelector('.olin-hint__why');
  if (label) label.textContent = rule.label;
  if (why) why.textContent = rule.why ? `. ${sentence(rule.why)}` : '';
  for (const link of bar.querySelectorAll('[data-olin-hint-link]')) {
    if (!(link instanceof HTMLAnchorElement)) continue;
    link.href = rule.url || '#';
    link.dataset.collection = rule.collection;
  }
}

/**
 * «Чому» comes as a sentence of its own after the name: the texts have dashes of their own.
 * @param {string} text
 */
function sentence(text) {
  const words = text.trim();
  const capital = words.charAt(0).toLocaleUpperCase() + words.slice(1);
  return /[.!?…]$/.test(capital) ? capital : `${capital}.`;
}

/**
 * On a phone the line waits at the bottom of the screen (snippet olin-cart-hints)
 * @param {HTMLElement} bar
 */
const docked = (bar) => getComputedStyle(bar).position === 'fixed';

/**
 * The line slides out from under the column row, or up from the bottom of a phone's screen.
 * @param {HTMLElement} bar
 */
function showHint(bar) {
  hintTurn += 1;
  for (const animation of bar.getAnimations({ subtree: true })) animation.cancel();
  delete bar.dataset.leaving;
  bar.hidden = false;
  markColumn();
  if (still()) return;
  bar.animate(
    [
      { opacity: 0, transform: `translateY(${docked(bar) ? 12 : -6}px)` },
      { opacity: 1, transform: 'none' },
    ],
    { duration: 220, easing: 'ease-out' }
  );
}

/**
 * The line slides back under the column row, or down off a phone's screen, and goes.
 * @param {HTMLElement} bar
 */
async function hideHint(bar) {
  const turn = (hintTurn += 1);
  bar.dataset.leaving = 'true';
  if (!still()) {
    const leaving = bar.animate(
      [
        { opacity: 1, transform: 'none' },
        { opacity: 0, transform: `translateY(${docked(bar) ? 12 : -6}px)` },
      ],
      { duration: 160, easing: 'ease-in', fill: 'forwards' }
    );
    await leaving.finished.catch(() => {});
    if (turn !== hintTurn) return;
    leaving.cancel();
  }
  delete bar.dataset.leaving;
  bar.hidden = true;
  markColumn();
}

/**
 * One rule gives way to the next like a departure board: the old words leave upwards, the new
 * ones come from below; the line itself stays.
 * @param {HTMLElement} bar
 */
async function swapHint(bar) {
  const turn = (hintTurn += 1);
  const text = bar.querySelector('.olin-hint__text');
  const animated = !still() && text instanceof HTMLElement;

  if (animated) {
    for (const animation of text.getAnimations()) animation.cancel();
    const leaving = text.animate(
      [
        { opacity: 1, transform: 'none' },
        { opacity: 0, transform: 'translateY(-8px)' },
      ],
      { duration: 140, easing: 'ease-in', fill: 'forwards' }
    );
    await leaving.finished.catch(() => {});
    if (turn !== hintTurn) return;
  }

  // The rule to show may have changed while the old one was leaving
  const handle = nextHint(bar);
  if (!handle) {
    if (animated) for (const animation of text.getAnimations()) animation.cancel();
    hideHint(bar);
    return;
  }
  fillHint(bar, handle);
  if (!animated) return;
  for (const animation of text.getAnimations()) animation.cancel();
  text.animate(
    [
      { opacity: 0, transform: 'translateY(8px)' },
      { opacity: 1, transform: 'none' },
    ],
    { duration: 200, easing: 'ease-out' }
  );
}

/**
 * «Вже маю», the cross and «Показати →».
 * @param {MouseEvent} event
 * @param {HTMLElement} control
 */
function onHintClick(event, control) {
  const bar = hintBar();
  if (!bar) return;

  if (control.hasAttribute('data-olin-hint-have')) {
    const skip = skipped();
    if (bar.dataset.rule) skip.add(bar.dataset.rule);
    const list = JSON.stringify([...skip]);
    store(localStorage, HINT_SKIPPED, list);
    if (stored(localStorage, HINT_SKIPPED) !== list) skippedHere = skip;
    renderHint();
    return;
  }

  if (control.hasAttribute('data-olin-hint-close')) {
    store(sessionStorage, HINT_CLOSED, '1');
    bar.dataset.closed = 'true';
    renderHint();
    return;
  }

  // «Показати →»: to the collection's shelf or column on this page; without one, the link opens it
  const handle = control.dataset.collection ?? '';
  const target = handle
    ? document.querySelector(
        `[data-shelf-collection="${CSS.escape(handle)}"], [data-group-collection="${CSS.escape(handle)}"]`
      )
    : null;
  if (!(target instanceof HTMLElement)) return;

  event.preventDefault();
  target.scrollIntoView({ behavior: still() ? 'auto' : 'smooth', block: 'start' });
  target.classList.remove('is-pointed');
  void target.offsetWidth;
  target.classList.add('is-pointed');
  target.addEventListener('animationend', () => target.classList.remove('is-pointed'), { once: true });
}

/**
 * A column is marked once its top reaches the line under the row, and stays marked until its top is this
 * far (px) back below the line. Before the row sticks, the first column's top sits exactly on that line, and
 * on a phone the scroll moves in fractions of a pixel: with one threshold both ways the mark switched on and
 * off many times a second.
 */
const HOLD = 16;

/**
 * The column on screen is marked in the sticky row, and the row scrolls to keep it in view. The first
 * column counts until another one passes the line.
 */
let frame = 0;
function markColumn() {
  cancelAnimationFrame(frame);
  frame = requestAnimationFrame(() => {
    const nav = document.querySelector('.collection-groups__nav');
    if (!(nav instanceof HTMLElement)) return;

    const chips = [...nav.querySelectorAll('a.olin-map__chip[href^="#"]')].filter((chip) => chip instanceof HTMLElement);
    // The line under the row covers the top of the shelves; at the bottom of a phone's screen it doesn't
    const bar = hintBar();
    const under = bar && !bar.hidden && !docked(bar) && bar.offsetParent !== null;
    const line = (under ? bar : nav).getBoundingClientRect().bottom + 16;

    /** @type {Element | null} */
    let current = chips[0] ?? null;
    for (const chip of chips.slice(1)) {
      const group = document.getElementById(chip.getAttribute('href')?.slice(1) ?? '');
      if (!group) continue;
      const edge = chip.classList.contains('is-active') ? line + HOLD : line;
      if (group.getBoundingClientRect().top <= edge) current = chip;
    }

    for (const chip of chips) {
      const active = chip === current;
      if (active && !chip.classList.contains('is-active')) keepInView(chip);
      chip.classList.toggle('is-active', active);
    }
  });
}

/** @param {Element} chip */
function keepInView(chip) {
  const row = chip.closest('.collection-groups__nav-list');
  if (!(row instanceof HTMLElement)) return;

  const rowBox = row.getBoundingClientRect();
  const box = chip.getBoundingClientRect();
  if (box.left >= rowBox.left && box.right <= rowBox.right) return;

  row.scrollBy({ left: box.left - rowBox.left - (rowBox.width - box.width) / 2, behavior: still() ? 'auto' : 'smooth' });
}

if (!window.olinCatalogReady) {
  window.olinCatalogReady = true;

  document.addEventListener('click', onClick);
  document.addEventListener(StandardEvents.cartLinesUpdate, onCartLinesUpdate);

  // Leaving with taps still in their pause: they go anyway
  window.addEventListener('pagehide', flushOnLeave);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flush();
  });
  window.addEventListener('pageshow', (event) => {
    if (!event.persisted) return;
    wanted.clear();
    refresh(true);
  });

  // On a computer the page scrolls inside .page-wrapper, on a phone the document scrolls
  let scrollTarget = getScrollEventTarget();
  scrollTarget.addEventListener('scroll', markColumn, { passive: true });
  scrollContainerMediaQuery.addEventListener('change', () => {
    scrollTarget.removeEventListener('scroll', markColumn);
    scrollTarget = getScrollEventTarget();
    scrollTarget.addEventListener('scroll', markColumn, { passive: true });
    markColumn();
  });

  // Clearing a filter brings the shelves back in new HTML: count again
  let pending = 0;
  new MutationObserver((records) => {
    const shelves = records.some((record) =>
      [...record.addedNodes].some((node) => node instanceof Element && node.querySelector?.('.olin-quick-add'))
    );
    if (!shelves) return;
    cancelAnimationFrame(pending);
    pending = requestAnimationFrame(adopt);
  }).observe(document.body, { childList: true, subtree: true });

  adopt();
}
